import {
  Avatar,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  TextField,
  type TextFieldProps,
} from "@mui/material";
import { useEffect, type FC } from "react";
import { config } from "../../../config";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import { SupabaseAccountLimitService } from "../../generic/server/supabase-account-service";
import type { ToTarget } from "../../to/use-to-target";
import { SupabaseSlackAliasWithDefaultService } from "../server/slack-supabase-service";

type SlackAliasSelectorProps = Omit<TextFieldProps, "value" | "onChange"> & {
  toTarget: ToTarget["toTarget"];
  reset?: () => void;
  value: string | null;
  onChange: (value: string | null) => void;
};

export const SlackAliasSelector: FC<SlackAliasSelectorProps> = ({
  toTarget,
  reset,
  value,
  onChange,
  ...props
}) => {
  const { loading: aliasesLoading, data: aliases } = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const { data, error } = await new SupabaseSlackAliasWithDefaultService(supabase).getAll(
        account_id,
        { is: [{ key: "deleted_at", value: null }], order: [{ column: "id" }] },
      );

      if (error || !data) return [];

      return data;
    },
    [],
  );

  const { loading: limitLoading, data: limit } = useSupabaseLoader(async ({ supabase }) => {
    const { data, error } = await new SupabaseAccountLimitService(supabase).getKey("slack_aliases");

    if (error || !data) return 0;

    return data.count;
  }, []);

  useEffect(() => {
    if (aliasesLoading || aliases === null) return;

    if (toTarget !== "channel" || aliases.length === 0) reset?.();
  }, [toTarget, aliases, reset, aliasesLoading]);

  if (aliasesLoading || limit === 0 || aliases === null || limitLoading) return null;

  return (
    <TextField
      onChange={(e) => onChange(e.target.value || null)}
      value={value ?? config.slack.defaultSlackAliasId}
      select
      label="From"
      disabled={toTarget !== "channel" || limit === 0}
      slotProps={{
        select: {
          renderValue: (val: unknown) => {
            const selected = aliases.find((alias) => alias.id === val);

            if (!selected || !selected.name || !selected.image)
              return <>{config.slack.defaultBotMomentPageTitle}</>;

            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Avatar
                  alt={selected.name}
                  src={selected.image}
                  sx={{ mr: 3, width: 24, height: 24 }}
                />
                <span>{selected.name}</span>
              </div>
            );
          },
        },
      }}
      {...props}
    >
      <MenuItem value={config.slack.defaultSlackAliasId}>
        {config.slack.defaultBotMomentPageTitle}
      </MenuItem>
      {aliases.map((alias) => (
        <MenuItem value={alias.id as string} key={alias.id as string}>
          {alias.image && alias.name && (
            <ListItemAvatar>
              <Avatar alt={alias.name} src={alias.image} sx={{ width: 24, height: 24 }} />
            </ListItemAvatar>
          )}
          <ListItemText>{alias.name}</ListItemText>
        </MenuItem>
      ))}
    </TextField>
  );
};
